/* Вид для чтения итога «Текста» (§7.4, resHTML прототипа): расшифровка репликами (спикер, моноширинное время; длинная свёрнута —
   «Показать всё · N реплик»), текст ролика абзацами, описание товара блоками со счётчиком и «Скопировать» у каждого, промпт.
   Подписи блоков — только из того, что известно: результат сервера и входы заказа. */
import { useRef, useState } from 'react';
import { uiFormat, uiText } from '../../../i18n';
import { ToolIcon } from '../../studio/circle/ToolIcon';
import { StudioIcon } from '../../studio/StudioIcon';
import { mmss, plural } from './text-model';
import { speakerName, type TextDocument } from './text-result-model';
import { useTextCopy } from './use-text-copy';

const COLLAPSE = 12;
export interface ReadingContext {
  /** Имя файла, длительность — из локальных сведений о выбранном файле */
  fileName?: string;
  durationMs?: number;
  language?: string;
  market?: string;
  tone?: string;
  style?: string;
  lang?: string;
  /** Подпись блока текста ролика («Весь текст», «Пересказ», «Тезисы») */
  ytKind?: string;
}
export function CopyButton({ id, text, node, copier }: { id: string; text: string; node?: () => Element | null; copier: ReturnType<typeof useTextCopy> }) {
  const ok = copier.done === id;
  return (
    <button className={`tt-cp${ok ? ' ok' : ''}`} type="button" aria-label={uiText(ok ? 'Скопировано' : 'Скопировать')} onClick={() => void copier.copy(id, text, node?.())}>
      {ok ? <StudioIcon kind="check" /> : <ToolIcon kind="copy" />}
      <span>{uiText(ok ? 'Скопировано' : 'Скопировать')}</span>
    </button>
  );
}
function Head({ title, sub, copy }: { title: string; sub?: string; copy?: React.ReactNode }) {
  return (
    <div className="tt-rh">
      <span className="m">
        <b>{uiText(title)}</b>
        {sub && <small>{sub}</small>}
      </span>
      {copy}
    </div>
  );
}
const chars = (value: string) => {
  const count = Array.from(value).length;
  return `${count} ${plural(count, 'знак', 'знака', 'знаков')}`;
};
export function TextReading({ document, context = {}, prompt = false }: { document: TextDocument; context?: ReadingContext; prompt?: boolean }) {
  const [all, setAll] = useState(false);
  const copier = useTextCopy();
  const nodes = useRef<Record<string, HTMLElement | null>>({});
  const hint = copier.failed && (
    <p className="hintp" role="status">
      {uiText('Текст выделен: скопируйте его вручную')}
    </p>
  );
  if (document.segments.length) {
    const speakers = new Set(document.segments.map(s => s.speaker).filter(Boolean));
    const timed = document.segments.some(s => s.start_ms !== undefined);
    const cut = document.segments.length > COLLAPSE && !all;
    const meta = [
      context.fileName,
      context.durationMs ? mmss(context.durationMs) : '',
      speakers.size ? `${speakers.size} ${plural(speakers.size, 'собеседник', 'собеседника', 'собеседников')}` : '',
      context.language?.toLowerCase(),
    ]
      .filter(Boolean)
      .join(' · ');
    const order = [...speakers];
    return (
      <div className="tt-res">
        <div className={`tt-rd${cut ? ' cut' : ''}`} id="ttRd" ref={node => void (nodes.current.transcript = node)}>
          <Head title="Расшифровка" sub={meta} />
          <div className="tt-tr">
            {document.segments.map((segment, index) => (
              <div className="tt-ln" key={index}>
                {(segment.speaker || segment.start_ms !== undefined) && (
                  <div className="w">
                    {segment.speaker && (
                      <>
                        {/^\d+$/.test(segment.speaker) && <span className={`av s${order.indexOf(segment.speaker)}`}>{segment.speaker}</span>}
                        <b>{speakerName(segment.speaker)}</b>
                      </>
                    )}
                    {timed && segment.start_ms !== undefined && <span className="tc mono">{mmss(segment.start_ms)}</span>}
                  </div>
                )}
                <p>{segment.text}</p>
              </div>
            ))}
          </div>
          {document.segments.length > COLLAPSE && (
            <button className="tt-more" type="button" onClick={() => setAll(value => !value)}>
              {all
                ? uiText('Свернуть')
                : uiFormat('Показать всё · {0} {1}', document.segments.length, plural(document.segments.length, 'реплика', 'реплики', 'реплик'))}
            </button>
          )}
        </div>
        {hint}
      </div>
    );
  }
  if (document.blocks.length) {
    const subs: Record<string, string> = {};
    for (const block of document.blocks) {
      if (block.label === 'Заголовок') subs[block.label] = [context.market, chars(block.text)].filter(Boolean).join(' · ');
      else if (block.label === 'Описание') subs[block.label] = [context.tone ? uiFormat('Стиль «{0}»', context.tone) : '', chars(block.text)].filter(Boolean).join(' · ');
      else if (block.label === 'Преимущества') subs[block.label] = `${block.items?.length ?? 0} ${plural(block.items?.length ?? 0, 'пункт', 'пункта', 'пунктов')}`;
      else subs[block.label] = `${block.items?.length ?? 0} ${plural(block.items?.length ?? 0, 'фраза', 'фразы', 'фраз')}`;
    }
    return (
      <div className="tt-res">
        {document.blocks.map(block => (
          <div className="tt-rd" key={block.label} ref={node => void (nodes.current[block.label] = node)}>
            <Head
              title={block.label}
              sub={subs[block.label]}
              copy={<CopyButton id={block.label} text={block.text} node={() => nodes.current[block.label] ?? null} copier={copier} />}
            />
            {block.label === 'Заголовок' ? (
              <p className="tt-h">{block.text}</p>
            ) : block.label === 'Преимущества' ? (
              <ul className="tt-ul">
                {block.items?.map((item, index) => <li key={index}>{item}</li>)}
              </ul>
            ) : block.label === 'Ключевые слова' ? (
              <div className="tt-kw">
                {block.items?.map((item, index) => <span key={index}>{item}</span>)}
              </div>
            ) : (
              <p className="tt-p">{block.text}</p>
            )}
          </div>
        ))}
        {hint}
      </div>
    );
  }
  const texts = document.variants.length ? document.variants : [document.transcript];
  return (
    <div className="tt-res">
      {texts.map((text, index) => {
        const id = `p${index}`;
        const kv = text.includes('\n') && text.split('\n').every(line => /^[^:]{2,16}:\s/.test(line));
        const title = prompt ? document.titles[index] || (texts.length > 1 ? uiFormat('Промпт {0}', index + 1) : 'Промпт') : context.ytKind || 'Текст';
        const sub = prompt ? [context.style, context.lang?.toLowerCase()].filter(Boolean).join(' · ') : '';
        return (
          <div className="tt-rd" key={id} ref={node => void (nodes.current[id] = node)}>
            <Head title={title} sub={sub} copy={prompt ? <CopyButton id={id} text={text} node={() => nodes.current[id] ?? null} copier={copier} /> : undefined} />
            {kv ? (
              <dl className="tt-kv">
                {text.split('\n').map((line, row) => {
                  const at = line.indexOf(':');
                  return (
                    <div key={row}>
                      <dt>{line.slice(0, at)}</dt>
                      <dd>{line.slice(at + 1).trim()}</dd>
                    </div>
                  );
                })}
              </dl>
            ) : (
              text.split(/\n{2,}/).map((paragraph, row) => (
                <p className="tt-p" key={row}>
                  {paragraph}
                </p>
              ))
            )}
          </div>
        );
      })}
      {hint}
    </div>
  );
}
